import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE, Overline } from "./motion";

export const KineticHero = ({
  overline,
  lines,
  sub,
  image,
  children,
  fullHeight = true,
  testId = "hero",
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      ref={ref}
      data-testid={testId}
      className={`relative overflow-hidden bg-[#0A0A0A] text-white ${
        fullHeight ? "min-h-[100svh]" : "min-h-[70svh]"
      } flex items-end`}
    >
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img
          src={image}
          alt=""
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/55" />
      <div className="js-grain absolute inset-0 opacity-[0.05]" />

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 sm:px-8 pb-20 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
        >
          <Overline dark>{overline}</Overline>
        </motion.div>

        <h1 className="mt-6 font-display tracking-tight leading-[0.95] text-[clamp(2.6rem,8vw,6.5rem)]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.3 + i * 0.14, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        {sub && (
          <motion.p
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-white/70"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          >
            {sub}
          </motion.p>
        )}

        {children && (
          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          >
            {children}
          </motion.div>
        )}
      </div>
    </section>
  );
};
